import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PlusIcon, ShieldCheckIcon } from 'lucide-react';
import { Button, ChoiceList, EmptyState, ErrorNotice, Panel, SkeletonTable } from '@bcecloud/portal-ui';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { errorMessage } from '@/lib/api';
import { readPermissionCatalog, type PermissionCatalogEntry } from '@/lib/events';
import {
  MY_IDENTITY_QUERY_KEY,
  ROLE_QUERY_KEY,
  accountTypeForTier,
  assignRole,
  canManageRoles,
  listRoles,
  listUserRoles,
  unassignRole,
  useMyIdentity,
  type Role,
} from '@/lib/roles';

/** Tiers that can hold roles; clients cannot (the API refuses them too). */
const ROLE_HOLDER_TIERS: ReadonlySet<string> = new Set(['contractor', 'internal', 'admin', 'superadmin']);

/** The labels of a role's permissions, for the line under its name. */
function permissionSummary(role: Role, catalog: readonly PermissionCatalogEntry[]): string {
  const labels = role.permissions.map((key) => catalog.find((entry) => entry.key === key)?.label ?? key);
  return labels.length === 0 ? 'No permissions yet' : labels.join(', ');
}

/** Whether the viewer holds every permission of a role: the API's rule for giving or removing it. */
function viewerCanHandOut(role: Role, catalog: readonly PermissionCatalogEntry[]): boolean {
  const held = new Set(catalog.filter((entry) => entry.held).map((entry) => entry.key));
  return role.permissions.every((key) => held.has(key));
}

/**
 * The person's roles on their profile: what each one gives them, and giving
 * or removing a role. Shown only while RBAC is switched on, to holders of
 * manage_roles, for staff and contractors.
 *
 * @param props - The person and their stored tier.
 * @returns The card, or null.
 */
export function RolesSection({
  userId,
  tier,
}: {
  readonly userId: string;
  readonly tier: string | undefined;
}): React.ReactElement | null {
  const queryClient = useQueryClient();
  const identity = useMyIdentity();
  const visible = canManageRoles(identity.data) && tier !== undefined && ROLE_HOLDER_TIERS.has(tier);
  const [assigning, setAssigning] = useState(false);

  const roles = useQuery({
    queryKey: ROLE_QUERY_KEY.forUser(userId),
    queryFn: ({ signal }) => listUserRoles(userId, signal),
    enabled: visible,
  });
  const catalog = useQuery({
    queryKey: ['permission-catalog'],
    queryFn: ({ signal }) => readPermissionCatalog(signal),
    staleTime: 5 * 60_000,
    enabled: visible,
  });

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ROLE_QUERY_KEY.forUser(userId) }),
      queryClient.invalidateQueries({ queryKey: ROLE_QUERY_KEY.list }),
      queryClient.invalidateQueries({ queryKey: ['user-permissions', userId] }),
      queryClient.invalidateQueries({ queryKey: MY_IDENTITY_QUERY_KEY }),
    ]);
  }
  const remove = useMutation({ mutationFn: (roleId: string) => unassignRole(userId, roleId), onSuccess: refresh });

  if (!visible) return null;

  return (
    <div className="mt-6">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="mb-1 text-portal-section font-bold tracking-[-0.4px]">Roles</h2>
          <p className="text-portal-small text-portal-muted">
            What this person can do comes from their roles, plus any extra permissions below.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setAssigning(true)}>
          <PlusIcon aria-hidden className="size-4" /> Give a role
        </Button>
      </div>

      {roles.isError || catalog.isError ? (
        <ErrorNotice title="Roles unavailable" message={errorMessage(roles.error ?? catalog.error)} />
      ) : roles.isPending || catalog.isPending ? (
        <SkeletonTable rows={1} columns={['w-[60%]', 'w-[20%]']} label="Loading roles…" />
      ) : roles.data.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<ShieldCheckIcon aria-hidden className="size-6" />}
            title="No roles yet"
            message="Give this person a role to grant them its permissions."
            action={<Button onClick={() => setAssigning(true)}>Give a role</Button>}
          />
        </Panel>
      ) : (
        <Panel className="divide-y divide-portal-line p-0">
          {roles.data.map((role) => {
            const pending = remove.isPending && remove.variables === role.id;
            return (
              <div key={role.id} className="flex items-center justify-between gap-4 p-5">
                <span className="min-w-0">
                  <Link to={`/settings/roles/${role.id}`} className="block text-portal-small font-bold text-brand hover:underline">
                    {role.name}
                  </Link>
                  <span className="mt-0.5 block text-portal-micro text-portal-muted">{permissionSummary(role, catalog.data)}</span>
                </span>
                {viewerCanHandOut(role, catalog.data) ? (
                  <Button variant="secondary" disabled={pending} onClick={() => remove.mutate(role.id)}>
                    {pending ? 'Removing…' : 'Remove'}
                  </Button>
                ) : null}
              </div>
            );
          })}
          {remove.isError ? (
            <p className="p-4 text-portal-micro font-semibold text-red-600">{errorMessage(remove.error)}</p>
          ) : null}
        </Panel>
      )}

      {assigning && catalog.data && roles.data ? (
        <GiveRoleDialog
          userId={userId}
          accountType={accountTypeForTier(tier)}
          held={roles.data}
          catalog={catalog.data}
          onClose={() => setAssigning(false)}
          onGiven={async () => {
            setAssigning(false);
            await refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/** Picks one role to give. Roles the viewer could not hand out are shown but disabled. */
function GiveRoleDialog({
  userId,
  accountType,
  held,
  catalog,
  onClose,
  onGiven,
}: {
  readonly userId: string;
  /** The person's account type; a role with a permission it may not hold is refused. */
  readonly accountType: string;
  readonly held: readonly Role[];
  readonly catalog: readonly PermissionCatalogEntry[];
  readonly onClose: () => void;
  readonly onGiven: () => Promise<void>;
}): React.ReactElement {
  const all = useQuery({ queryKey: ROLE_QUERY_KEY.list, queryFn: ({ signal }) => listRoles(signal) });
  const heldIds = new Set(held.map((role) => role.id));
  const available = (all.data ?? []).filter((role) => !heldIds.has(role.id));
  // Nothing is preselected: giving a role (Administrator, say) is a deliberate choice.
  const [selected, setSelected] = useState<string>('');
  const give = useMutation({ mutationFn: () => assignRole(userId, selected), onSuccess: onGiven });

  /** Why a role can't be given here, or null when it can. */
  function blockedReason(role: Role): string | null {
    if (!viewerCanHandOut(role, catalog)) return 'Includes permissions you don’t hold, so you can’t give it.';
    const fits = role.permissions.every(
      (key) => catalog.find((entry) => entry.key === key)?.assignableTo.includes(accountType) ?? true,
    );
    return fits ? null : 'Includes permissions this person’s account can’t hold.';
  }

  return (
    <Dialog open onOpenChange={(next) => (!next && !give.isPending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>Give a role</DialogTitle>
        <div className="mt-2 space-y-4">
          {all.isError ? (
            <ErrorNotice inline title="Roles unavailable" message={errorMessage(all.error)} />
          ) : all.isPending ? (
            <SkeletonTable rows={2} columns={['w-[70%]']} label="Loading roles…" />
          ) : available.length === 0 ? (
            <EmptyState
              title="Nothing left to give"
              message="This person already has every role. Add a new one under Settings → Roles."
            />
          ) : (
            <ChoiceList
              label="Role to give"
              value={selected}
              onChange={setSelected}
              options={available.map((role) => {
                const reason = blockedReason(role);
                return {
                  key: role.id,
                  label: role.name,
                  detail: reason ?? permissionSummary(role, catalog),
                  disabled: reason !== null,
                };
              })}
            />
          )}
          {give.isError ? <ErrorNotice inline title="Not given" message={errorMessage(give.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={give.isPending}>
              Cancel
            </Button>
            <Button onClick={() => give.mutate()} disabled={selected === '' || give.isPending}>
              {give.isPending ? 'Giving…' : 'Give role'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
